<template>
  <div class="time-off-policy-setup">
    <!-- eslint-disable vue/no-mutating-props -->
    <div class="mb-24">
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required|min:3|max:50"
        name="name"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required">Policy name*</label>
          <AppInput
            v-model="timeOffPolicy.name"
            placeholder="Enter the policy name"
            data-testid="policy-name-input"
            :disabled="isViewMode"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
    </div>
    <div class="mb-15">
      <span class="section-header">
        Accrual
      </span>
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required"
        name="accrual_type"
        :disabled="isViewMode"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required">Schedule of accrual</label>
          <div v-show="editing">
            {{ accrualType }}
            <p class="accrual-policy-change-notice">Updates to accrual settings are not retroactive</p>
          </div>
          <AppPill
            v-show="!editing"
            v-model="timeOffPolicy.accrual_type"
            data-testid="policy-accrual-type-pills"
            :options="dropdownOptions.accrual_type"
            name="time_off_policy[accrual_type]"
            aria-label="Schedule of accrual"
            :tooltips="accrualTypeTooltips"
            :disabled="isViewMode"
            @input="setSubfieldsVisibility"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>

        <div
          v-if="hasAccrualScheduleWarning"
          class="seats-increase-warning-box dialog-warning blue"
          style="align-items: center;"
        >
          <i class="d-flex hi hi-info-filled hi-12 text-blue">
          </i>
          <div v-if="accrualTypeId === 1">
            Time off hours accrue only when hours are <strong>marked as paid</strong> —
            they’re included in a payment record, not necessarily paid out.
            <a
              href="https://support.hubstaff.com/paid-time-off-holidays-and-time-off-requests/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
              <i class="hi-external hi-12 text-blue"></i>
            </a>
          </div>
          <div v-if="accrualTypeId === 3">
            Balances are prorated and may take up to <strong>24 hours</strong> to display for members added to
            a time off policy set to “Policy joined date”.
          </div>
        </div>
      </ValidationProvider>
    </div>
    <div
      v-if="showMonthlyAccrualFields"
      class="row mb-15"
    >
      <div class="col-sm-6">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="hours"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Accrual amount*</label>
            <i
              v-tooltip="tooltipOptions"
              :title="'Enter the amount of hours this policy will allow. The amount type selected will not change the schedule of accrual'"
              class="hi hi-info-filled hi-12 hi-tooltip ml-4 time-off-policy-tooltip"
            />
            <div class="input-group">
              <AppInput
                v-model.number="timeOffPolicy.accrual_policy.hours"
                type="number"
                min="0"
                step="0.01"
                :disabled="isViewMode"
                data-testid="policy-accrual-amount-input"
              />
              <select
                v-model="timeOffPolicy.accrual_policy.accrual_period"
                :disabled="isViewMode"
                class="input-group-addon accrual-period-input-group no-wrap"
                data-testid="policy-accrual-period-select"
              >
                <option
                  v-for="option in monthlyAccrualPeriodOptions"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
            <span
              v-if="errors && errors[0]"
              class="help-block"
            >{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="col-sm-6 mt-16 mt-md-0">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="accrual_day"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Accrual day</label>
            <i
              v-tooltip="tooltipOptions"
              :title="'Select which day of the month the days will become available'"
              class="hi hi-info-filled hi-12 hi-tooltip ml-4 time-off-policy-tooltip"
            />
            <AppSelect
              v-model="timeOffPolicy.accrual_policy.accrual_day"
              :options="monthlyAccrualDayOptions"
              :select2-options="select2Options"
              :async="false"
              :disabled="isViewMode"
              data-testid="policy-accrual-day-select"
            />
          </div>
        </ValidationProvider>
      </div>
      <div class="col-sm-12 mt-24">
        <div class="accrual-calculator-card">
          <div class="accrual-calculator-header">
            <div class="accrual-calculator-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 18C1.45 18 0.975 17.8083 0.575 17.425C0.191667 17.025 0 16.55 0 16V2C0 1.45 0.191667 0.983333 0.575 0.599999C0.975 0.199999 1.45 -1.43051e-06 2 -1.43051e-06H16C16.55 -1.43051e-06 17.0167 0.199999 17.4 0.599999C17.8 0.983333 18 1.45 18 2V16C18 16.55 17.8 17.025 17.4 17.425C17.0167 17.8083 16.55 18 16 18H2ZM2 4H16V2H2V4ZM8 12H2V16H8V12ZM10 12V16H16V12H10ZM8 10V6H2V10H8ZM10 10H16V6H10V10Z"
                  fill="#0A4B96"
                />
              </svg>
            </div>
            <div class="accrual-calculator-title">
              <div>Accrual</div>
              <div>calculator</div>
            </div>
          </div>
          <div class="accrual-calculator-metric">
            <span class="accrual-calculator-label">PER MONTH</span>
            <span
              v-if="monthlyAccrualCalculator.perMonth.hours"
              class="accrual-calculator-value"
              data-testid="accrual-calculator-monthly-value"
            >
              <strong>{{ monthlyAccrualCalculator.perMonth.hours }} {{ monthlyAccrualCalculator.perMonth.hourLabel }}</strong>: {{ monthlyAccrualCalculator.perMonth.days }} {{ monthlyAccrualCalculator.perMonth.dayLabel }}
            </span>
            <span
              v-else
              class="accrual-calculator-value"
            >
              <strong>--</strong>
            </span>
          </div>
          <div class="accrual-calculator-metric">
            <span class="accrual-calculator-label">PER YEAR</span>
            <span
              v-if="monthlyAccrualCalculator.perYear.hours"
              class="accrual-calculator-value"
              data-testid="accrual-calculator-yearly-value"
            >
              <strong>{{ monthlyAccrualCalculator.perYear.hours }} {{ monthlyAccrualCalculator.perYear.hourLabel }}</strong>: {{ monthlyAccrualCalculator.perYear.days }} {{ monthlyAccrualCalculator.perYear.dayLabel }}
            </span>
            <span
              v-else
              class="accrual-calculator-value"
            >
              <strong>--</strong>
            </span>
          </div>
        </div>
      </div>
      <div class="col-sm-12 mt-24">
        <AppCheckbox
          v-model="timeOffPolicy.accrual_policy.prorate_on_start"
          class="form-checkbox"
          data-testid="policy-prorate-on-start-checkbox"
          :disabled="isViewMode"
        >
          <span>Allow prorated time based on start date</span>
          <i
            v-tooltip="tooltipOptions"
            :title="'Time earned mid-month will be prorated based on start date.'"
            class="hi hi-info-filled hi-12 ml-4 time-off-policy-tooltip"
          />
        </AppCheckbox>
      </div>
    </div>
    <div
      v-if="showAmountAccrued"
      class="mb-8"
    >
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required"
        name="hours_per"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required">Amount accrued*</label>
          <div class="row">
            <div class="col-sm-6 pr-lg-0">
              <div class="d-flex input-group">
                <AppInput
                  v-model.number="timeOffPolicy.accrual_policy.hours_per"
                  :disabled="isViewMode"
                  data-testid="policy-hours-per-input"
                />
                <span class="input-group-addon corner-md-r no-wrap">hour(s) accrued for every</span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
            <div class="col-sm-6 pl-lg-0">
              <div class="d-flex input-group corner-md-l">
                <AppInput
                  v-model.number="timeOffPolicy.accrual_policy.hours_worked"
                  :disabled="isViewMode"
                  data-testid="policy-hours-worked-input"
                />
                <span class="input-group-addon no-wrap">hours worked</span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </div>
        </div>
      </ValidationProvider>
    </div>
    <div
      v-if="accrualTypeValue === '0'"
      class="row"
    >
      <div class="col-sm-12">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          :rules="betweenOrNullRule"
          name="starting_balance"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Starting balance</label>
            <div class="input-group">
              <AppInput
                v-model="timeOffPolicy.accrual_policy.starting_balance"
                class="rounded-input"
                placeholder="Enter amount of hours"
                :disabled="isViewMode"
              />
            </div>
            <span
              v-if="errors && errors[0]"
              class="help-block"
            >{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>
    <div
      v-if="showMaxAccrualAmount"
      class="row"
    >
      <div class="col-sm-6">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="maximum_to_accrue"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Maximum accrual amount*</label>
            <div class="input-group">
              <AppInput
                v-model="timeOffPolicy.accrual_policy.hours_per_year"
                data-testid="policy-max-accrual-input"
                :disabled="isViewMode"
              />
              <span class="input-group-addon">hours per year</span>
            </div>
            <span
              v-if="errors && errors[0]"
              class="help-block"
            >{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="flex-50 mb-8"></div>
    </div>
    <div class="options-section">
      <p class="section-header">Options</p>
      <div class="option-card-grid">
        <div
          class="option-card"
          :class="{ 'option-card--active': Boolean(timeOffPolicy.allow_negative_balances) }"
          role="button"
          tabindex="0"
          @click="toggleOption('allow_negative_balances')"
          @keydown.enter.space.prevent="toggleOption('allow_negative_balances')"
        >
          <AppCheckbox
            v-model="timeOffPolicy.allow_negative_balances"
            class="option-checkbox"
            :disabled="isViewMode"
            @click.native.stop
          >
            Allow negative balances
          </AppCheckbox>
          <p class="option-description">{{ optionDescription('allow_negative_balances') }}</p>
        </div>
        <div
          class="option-card"
          :class="{ 'option-card--active': Boolean(timeOffPolicy.balance_rolls_over_annually) }"
          role="button"
          tabindex="0"
          @click="toggleOption('balance_rolls_over_annually')"
          @keydown.enter.space.prevent="toggleOption('balance_rolls_over_annually')"
        >
          <AppCheckbox
            v-model="timeOffPolicy.balance_rolls_over_annually"
            class="option-checkbox"
            :disabled="isViewMode"
            @click.native.stop
          >
            Balance rolls over annually
          </AppCheckbox>
          <p class="option-description">{{ optionDescription('balance_rolls_over_annually') }}</p>
        </div>
        <div
          class="option-card"
          :class="{ 'option-card--active': Boolean(timeOffPolicy.requires_approval) }"
          role="button"
          tabindex="0"
          @click="toggleOption('requires_approval')"
          @keydown.enter.space.prevent="toggleOption('requires_approval')"
        >
          <AppCheckbox
            v-model="timeOffPolicy.requires_approval"
            class="option-checkbox"
            :disabled="isViewMode"
            @click.native.stop
          >
            Require approval
          </AppCheckbox>
          <p class="option-description">{{ optionDescription('requires_approval') }}</p>
        </div>
      </div>
      <div class="paid-section">
        <div class="paid-section-heading">Paid or unpaid</div>
        <div
          class="paid-toggle"
          role="group"
          aria-label="Paid or unpaid"
        >
          <button
            v-for="option in paidOptions"
            :key="option.id"
            type="button"
            class="paid-toggle-button"
            :class="{ 'paid-toggle-button--active': isPaidOptionActive(option.id) }"
            :disabled="isViewMode"
            @click="selectPaidOption(option.id)"
          >
            {{ option.text }}
          </button>
        </div>
        <p class="option-description paid-description">
          Hours in approved time off requests will count towards amounts owed
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { extend } from 'vee-validate';
import { min, max, between } from 'vee-validate/dist/rules';
import AppCheckbox from '~/components/AppCheckbox.vue';
import AppPill from '../AppPill.vue';

extend('min', {
  ...min,
  message: 'is too short (minimum is three characters)',
});

extend('max', {
  ...max,
  message: 'is too long (maximum is fifty characters)',
});

extend('between_or_null', {
  ...between,
  validate: (value, { min, max }) => {
    if (value === null || value === '') {
      return true;
    }

    return Number(value) >= min && Number(value) <= max;
  },
  params: ['min', 'max'],
  message: 'must be between {min} and {max}',
});

export default {
  name: 'TimeOffPolicySetupForm',
  components: {
    AppCheckbox,
    AppPill,
  },
  props: {
    timeOffPolicy: {
      type: Object,
      required: true,
    },
    dropdownOptions: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      required: true,
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      select2Options: Object.freeze({
        allowClear: false,
        multiple: false,
        width: '100%',
      }),
      showMaxAccrualAmount: false,
      showAmountAccrued: false,
      showMonthlyAccrualFields: false,
      tooltipOptions: {
        placement: 'top',
      },
      totalHoursPerYear: '8760',
    };
  },
  computed: {
    accrualTypeId() {
      return Number(this.timeOffPolicy.accrual_type);
    },
    accrualTypeValue() {
      const { accrual_type: type } = this.timeOffPolicy;

      if (type === null || type === undefined) {
        return '';
      }

      return String(type);
    },
    accrualType() {
      const option = this.dropdownOptions.accrual_type.find((option) => option.id === this.accrualTypeId);

      return option?.text || '';
    },
    monthlyAccrualPeriodOptions() {
      return this.dropdownOptions.monthly_accrual_periods || [];
    },
    monthlyAccrualDayOptions() {
      return this.dropdownOptions.monthly_accrual_days || [];
    },
    paidOptions() {
      return this.dropdownOptions.paid || [];
    },
    defaultMonthlyAccrualPeriodId() {
      const fallback = this.monthlyAccrualPeriodOptions[0];
      const hoursPerMonthOption = this.monthlyAccrualPeriodOptions.find((option) => option.id === 'month');

      return (hoursPerMonthOption || fallback || {}).id || '';
    },
    defaultMonthlyAccrualDayId() {
      const fallback = this.monthlyAccrualDayOptions[0];
      const monthlyAnniversaryOption = this.monthlyAccrualDayOptions.find((option) => option.id === 'monthly_anniversary');

      return (monthlyAnniversaryOption || fallback || {}).id || '';
    },
    hoursPerDay() {
      return Number(this.timeOffPolicy.hours_per_day) || 8;
    },
    hasMonthlyAccrualAmount() {
      const value = this.timeOffPolicy.accrual_policy?.hours;

      return value !== undefined && value !== null && value !== '';
    },
    monthlyAccrualCalculator() {
      const policy = this.timeOffPolicy.accrual_policy || {};
      const amount = Number(policy.hours);
      const normalizedAmount = Number.isFinite(amount) ? amount : 0;
      const period = policy.accrual_period || this.defaultMonthlyAccrualPeriodId || 'month';
      const monthlyHours = period === 'year' ? normalizedAmount / 12 : normalizedAmount;
      const yearlyHours = period === 'year' ? normalizedAmount : normalizedAmount * 12;

      return {
        perMonth: this.buildCalculatorEntry(monthlyHours),
        perYear: this.buildCalculatorEntry(yearlyHours),
      };
    },
    accrualTypeTooltips() {
      return {
        0: 'Time off will not be automatically accrued for this policy',
        1: 'Hours are accrued when time worked is marked as paid',
        2: 'Hours are accrued when the member joins the policy, and on January 1st of every year',
        3: 'Hours are accrued when the member is added to the policy, at a prorated amount for the year. Hours are also accrued on January 1st of every year.',
        4: 'Hours are accrued on the monthly accrual day',
      };
    },
    hasAccrualScheduleWarning() {
      const type = Number(this.timeOffPolicy.accrual_type);

      return (type === 1 || type === 3);
    },
    betweenOrNullRule() {
      return `between_or_null:0,${this.totalHoursPerYear}`;
    },
    isViewMode() {
      return this.viewMode === true || this.viewMode === 'true';
    }
  },
  created() {
    this.setSubfieldsVisibility();
  },
  methods: {
    setSubfieldsVisibility() {
      const accrualTypeValue = this.accrualTypeValue;

      this.showMaxAccrualAmount = accrualTypeValue !== '0' && accrualTypeValue !== '4';
      this.showAmountAccrued = accrualTypeValue === '1';
      this.showMonthlyAccrualFields = accrualTypeValue === '4';

      if (this.showMonthlyAccrualFields) {
        this.ensureMonthlyDefaults();
      }
    },
    ensureMonthlyDefaults() {
      const policy = this.timeOffPolicy.accrual_policy;

      if (!policy) {
        return;
      }

      if (!policy.accrual_period && this.defaultMonthlyAccrualPeriodId) {
        this.$set(policy, 'accrual_period', this.defaultMonthlyAccrualPeriodId);
      }

      if (!policy.accrual_day && this.defaultMonthlyAccrualDayId) {
        this.$set(policy, 'accrual_day', this.defaultMonthlyAccrualDayId);
      }

      if (typeof policy.prorate_on_start === 'undefined') {
        this.$set(policy, 'prorate_on_start', true);
      }
    },
    optionDescription(optionKey) {
      const options = this.dropdownOptions[optionKey] || [];
      const affirmativeOption = options.find((option) => option.id === true);

      return affirmativeOption?.description || '';
    },
    toggleOption(optionKey) {
      if (this.viewMode) { return };

      const currentValue = Boolean(this.timeOffPolicy[optionKey]);

      this.$set(this.timeOffPolicy, optionKey, !currentValue);
    },
    selectPaidOption(value) {
      if (this.viewMode) { return };

      this.$set(this.timeOffPolicy, 'paid', value);
    },
    isPaidOptionActive(value) {
      return this.timeOffPolicy.paid === value;
    },
    buildCalculatorEntry(hours) {
      const safeHours = Number.isFinite(hours) && hours > 0 ? hours : 0;
      const roundedHours = Math.round(safeHours * 100) / 100;
      const days = this.hoursPerDay > 0 ? roundedHours / this.hoursPerDay : 0;
      const roundedDays = Math.round(days * 100) / 100;

      return {
        hours: this.formatCalculatorValue(roundedHours),
        days: this.formatCalculatorValue(roundedDays),
        hourLabel: Number(roundedHours) === 1 ? 'hour' : 'hours',
        dayLabel: Number(roundedDays) === 1 ? 'day' : 'days',
      };
    },
    formatCalculatorValue(value) {
      if (!Number.isFinite(value) || value <= 0) {
        return null;
      }

      const rounded = Math.round(value * 100) / 100;

      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.0+$|0+$/u, '');
    },
  },
};
</script>
